/**
 * Inline measurement warning chip — surfaces yellow ("outside typical
 * range") or red ("physically impossible / contradicts another field")
 * issues right next to the value, without forcing a layout change.
 *
 * Used like:
 *
 *   <MeasurementInput
 *     value={chest}
 *     onChange={setChest}
 *     warning={validateChest(chest)}     // see validateMeasurement helper below
 *   />
 *
 * The helper `validateMeasurement(field, value, allMeasurements)` returns one
 * of:
 *   null                       // value is in the normal range, no chip
 *   { level: "yellow", note }  // outside typical range (warn)
 *   { level: "red", note }     // impossible or contradictory (block-worthy)
 */
import { AlertTriangle, AlertCircle } from "lucide-react";

export function MeasurementWarning({ warning }) {
  if (!warning) return null;
  const isRed = warning.level === "red";
  const Icon = isRed ? AlertCircle : AlertTriangle;
  return (
    <span
      data-testid={`measurement-warning-${warning.level}`}
      title={warning.note}
      className={
        `inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ` +
        (isRed
          ? "bg-red-500/15 text-red-400 border border-red-500/30"
          : "bg-amber-500/15 text-amber-400 border border-amber-500/30")
      }
    >
      <Icon size={10} />
      {warning.note}
    </span>
  );
}

/**
 * Pure validation — given a field name, a value, and the full measurements
 * map (for cross-field checks), returns the warning or null.
 *
 * Ranges are derived from common bespoke industry norms; tweak per shop.
 * Cross-field rules surface as red (e.g. "thigh > seat" is physically
 * impossible).
 *
 * The function tolerates string / numeric input and any unit (assumes
 * inches by default, but if value ends in "cm" converts inline).
 */
function toInches(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  const s = String(v).trim().toLowerCase();
  const num = parseFloat(s.replace(/[^0-9.\-]/g, ""));
  if (Number.isNaN(num)) return null;
  if (s.includes("cm")) return num / 2.54;
  return num;
}

/** Typical bespoke-suit measurement ranges in inches. */
const RANGES = {
  // Upper body
  chest:        { min: 32, max: 56,  warnMin: 34, warnMax: 52 },
  waist:        { min: 26, max: 56,  warnMin: 28, warnMax: 50 },
  seat:         { min: 32, max: 60,  warnMin: 34, warnMax: 54 },
  shoulder:     { min: 14, max: 22,  warnMin: 16, warnMax: 20 },
  sleeve:       { min: 22, max: 38,  warnMin: 23, warnMax: 36 },
  jacket_length:{ min: 24, max: 36,  warnMin: 26, warnMax: 34 },
  neck:         { min: 13, max: 22,  warnMin: 14, warnMax: 20 },
  // Trouser
  thigh:        { min: 18, max: 36,  warnMin: 19, warnMax: 32 },
  knee:         { min: 12, max: 28,  warnMin: 14, warnMax: 24 },
  bottom:       { min: 12, max: 28,  warnMin: 13, warnMax: 24 },
  inseam:       { min: 24, max: 38,  warnMin: 26, warnMax: 36 },
  outseam:      { min: 34, max: 50,  warnMin: 36, warnMax: 48 },
  front_rise:   { min: 8,  max: 16,  warnMin: 9,  warnMax: 15 },
  back_rise:    { min: 13, max: 22,  warnMin: 14, warnMax: 20 },
  // Customer overall
  height:       { min: 54, max: 84,  warnMin: 60, warnMax: 80 }, // 4'6"–7'
  weight:       { min: 80, max: 400, warnMin: 100, warnMax: 350 }, // lbs
};

export function validateMeasurement(field, rawValue, all = {}) {
  const v = toInches(rawValue);
  if (v === null || Number.isNaN(v)) return null;

  // ----- Cross-field red flags (impossible / contradictory) -----
  if (field === "thigh") {
    const seat = toInches(all.seat);
    if (seat && v > seat) {
      return { level: "red", note: `Thigh > seat (${v} > ${seat})` };
    }
  }
  if (field === "waist") {
    const chest = toInches(all.chest);
    if (chest && v > chest + 10) {
      return { level: "red", note: `Waist much larger than chest — check entry` };
    }
  }
  if (field === "inseam") {
    const height = toInches(all.height);
    if (height && v > height * 0.65) {
      return { level: "red", note: `Inseam > 65% of height — likely typo` };
    }
  }
  if (field === "knee") {
    const thigh = toInches(all.thigh);
    if (thigh && v > thigh + 2) {
      return { level: "red", note: `Knee > thigh — physically impossible` };
    }
  }
  if (field === "bottom") {
    const knee = toInches(all.knee);
    if (knee && v > knee + 2) {
      return { level: "red", note: `Bottom > knee + 2" — check entry` };
    }
  }

  // ----- Single-field range check -----
  const range = RANGES[field];
  if (!range) return null;
  if (v < range.min || v > range.max) {
    return { level: "red", note: `Out of expected range (${range.min}–${range.max})` };
  }
  if (v < range.warnMin || v > range.warnMax) {
    return { level: "yellow", note: `Outside typical range (${range.warnMin}–${range.warnMax})` };
  }
  return null;
}

export default MeasurementWarning;
